import { keysBetween } from "./sortKey";
import type { FsNode } from "./types";
import { isFolder } from "./types";

/**
 * The Finder column-browser model, as pure functions over the tree.
 *
 * Selection is a *path*: the chain of selected node ids from the root down. That
 * single piece of state gives you the columns (one per selected folder), the
 * breadcrumb, and the inspector target, so nothing can disagree about what is
 * selected.
 */

/**
 * The minimum a node needs for the tree-building and move helpers below. The
 * case filesystem (`FsNode`) and the workspace tree (`WsNode`) both fit, so
 * one optimistic-move implementation serves both panes.
 */
export type TreeNodeLike = {
  id: string;
  parent_id: string | null;
  kind: string;
  sort_key: string;
  is_deleted?: boolean;
};

export type FsTree<N extends TreeNodeLike = FsNode> = {
  /** Direct children of each parent, in `sort_key` order. `null` is the root. */
  childrenByParent: Map<string | null, N[]>;
  byId: Map<string, N>;
};

export type Column = {
  parentId: string | null;
  nodes: FsNode[];
  /** The node selected in this column, if any. */
  selectedId: string | null;
};

export const buildTree = <N extends TreeNodeLike>(nodes: N[]): FsTree<N> => {
  const childrenByParent = new Map<string | null, N[]>();
  const byId = new Map<string, N>();

  nodes.forEach((node) => {
    if (node.is_deleted) return;
    byId.set(node.id, node);
    const key = node.parent_id ?? null;
    const siblings = childrenByParent.get(key);
    if (siblings) siblings.push(node);
    else childrenByParent.set(key, [node]);
  });

  childrenByParent.forEach((siblings) =>
    siblings.sort((a, b) =>
      a.sort_key < b.sort_key ? -1 : a.sort_key > b.sort_key ? 1 : 0
    )
  );

  return { childrenByParent, byId };
};

export const childrenOf = <N extends TreeNodeLike>(
  tree: FsTree<N>,
  parentId: string | null
): N[] => tree.childrenByParent.get(parentId) ?? [];

/**
 * Drops path entries that no longer exist or no longer descend from their
 * predecessor. A path can go stale from a move, a delete, or another user's
 * edit, and a stale path must degrade to a shorter valid one rather than render
 * an empty column.
 */
export const prunePath = (tree: FsTree, path: string[]): string[] => {
  const pruned: string[] = [];
  let parentId: string | null = null;

  for (const nodeId of path) {
    const node = tree.byId.get(nodeId);
    if (!node || (node.parent_id ?? null) !== parentId) break;
    pruned.push(nodeId);
    if (!isFolder(node)) break;
    parentId = nodeId;
  }

  return pruned;
};

/**
 * One column per level: the root, plus the children of every selected folder.
 * A selected file adds no column — the inspector takes that slot.
 */
export const columnsForPath = (tree: FsTree, path: string[]): Column[] => {
  const valid = prunePath(tree, path);
  const columns: Column[] = [
    {
      parentId: null,
      nodes: childrenOf(tree, null),
      selectedId: valid[0] ?? null
    }
  ];

  valid.forEach((nodeId, index) => {
    const node = tree.byId.get(nodeId);
    if (!node || !isFolder(node)) return;
    columns.push({
      parentId: nodeId,
      nodes: childrenOf(tree, nodeId),
      selectedId: valid[index + 1] ?? null
    });
  });

  return columns;
};

/** The node the inspector describes: the deepest selected node. */
export const selectedNode = (tree: FsTree, path: string[]): FsNode | null => {
  const valid = prunePath(tree, path);
  if (valid.length === 0) return null;
  return tree.byId.get(valid[valid.length - 1]) ?? null;
};

/**
 * The selection path that reveals `nodeId`: its ancestors, root first, then the
 * node. Empty when the node is not in the tree, so a stale deep link leaves the
 * current selection alone rather than clearing it.
 */
export const pathToNode = (tree: FsTree, nodeId: string): string[] => {
  const path: string[] = [];
  let node = tree.byId.get(nodeId);
  while (node) {
    path.unshift(node.id);
    node = node.parent_id ? tree.byId.get(node.parent_id) : undefined;
  }
  return path;
};

/** Ancestors of the selection, root first, for the breadcrumb. */
export const breadcrumbFor = (tree: FsTree, path: string[]): FsNode[] =>
  prunePath(tree, path)
    .map((nodeId) => tree.byId.get(nodeId))
    .filter((node): node is FsNode => Boolean(node));

const siblingsFor = (tree: FsTree, path: string[]): FsNode[] => {
  const parentId = path.length > 1 ? path[path.length - 2] : null;
  return childrenOf(tree, parentId);
};

/** Selects a node in the column at `depth`, discarding any deeper selection. */
export const selectAtDepth = (
  path: string[],
  depth: number,
  nodeId: string
): string[] => [...path.slice(0, depth), nodeId];

export type NavigationKey =
  | "ArrowUp"
  | "ArrowDown"
  | "ArrowLeft"
  | "ArrowRight"
  | "Enter"
  | "Home"
  | "End";

/**
 * Applies one key press, returning the new path. Returns the input unchanged
 * when the key does not move, so a caller can decide whether to preventDefault.
 */
export const navigate = (
  tree: FsTree,
  path: string[],
  key: NavigationKey
): string[] => {
  const valid = prunePath(tree, path);

  if (key === "ArrowLeft") {
    // Left steps out to the parent, which is already selected one column over.
    return valid.length > 1
      ? valid.slice(0, -1)
      : valid.length === 1
      ? []
      : valid;
  }

  if (key === "ArrowRight" || key === "Enter") {
    const node = selectedNode(tree, valid);
    if (!node || !isFolder(node)) return valid;
    const children = childrenOf(tree, node.id);
    if (children.length === 0) return valid;
    return [...valid, children[0].id];
  }

  const siblings =
    valid.length === 0 ? childrenOf(tree, null) : siblingsFor(tree, valid);
  if (siblings.length === 0) return valid;

  const depth = Math.max(valid.length - 1, 0);
  const currentIndex =
    valid.length === 0 ? -1 : siblings.findIndex((n) => n.id === valid[depth]);

  let nextIndex = currentIndex;
  if (key === "ArrowDown")
    nextIndex = Math.min(currentIndex + 1, siblings.length - 1);
  if (key === "ArrowUp") nextIndex = Math.max(currentIndex - 1, 0);
  if (key === "Home") nextIndex = 0;
  if (key === "End") nextIndex = siblings.length - 1;

  if (nextIndex === currentIndex || nextIndex < 0) return valid;
  return selectAtDepth(valid, depth, siblings[nextIndex].id);
};

/**
 * The enclosing folder of the selection, for Cmd-Up. Selecting the parent is
 * what "go to enclosing folder" means in a column browser: the parent already
 * owns a column, so the view moves left rather than replacing itself.
 */
export const enclosingFolder = (tree: FsTree, path: string[]): string[] => {
  const valid = prunePath(tree, path);
  return valid.length > 0 ? valid.slice(0, -1) : valid;
};

export type RangeSelection = {
  /** Path with focus moved to the newly reached row. */
  path: string[];
  /** Every selected id in the active column, in column order. */
  ids: string[];
};

/**
 * Shift-arrow range selection inside the active column.
 *
 * The anchor is where the range started — the row that was focused when the
 * user first held shift — so reversing direction shrinks the range back toward
 * it instead of stranding rows behind the cursor.
 */
export const extendSelection = (
  tree: FsTree,
  path: string[],
  anchorId: string | null,
  key: "ArrowUp" | "ArrowDown"
): RangeSelection => {
  const valid = prunePath(tree, path);
  const siblings =
    valid.length === 0 ? childrenOf(tree, null) : siblingsFor(tree, valid);
  if (siblings.length === 0) return { path: valid, ids: [] };

  const depth = Math.max(valid.length - 1, 0);
  const focusedId = valid[depth];
  const focusIndex = siblings.findIndex((n) => n.id === focusedId);
  if (focusIndex < 0) return { path: valid, ids: [] };

  const nextIndex =
    key === "ArrowDown"
      ? Math.min(focusIndex + 1, siblings.length - 1)
      : Math.max(focusIndex - 1, 0);

  const anchorIndex = anchorId
    ? siblings.findIndex((n) => n.id === anchorId)
    : focusIndex;
  const from = anchorIndex < 0 ? focusIndex : anchorIndex;

  const [low, high] = from <= nextIndex ? [from, nextIndex] : [nextIndex, from];
  return {
    path: selectAtDepth(valid, depth, siblings[nextIndex].id),
    ids: siblings.slice(low, high + 1).map((n) => n.id)
  };
};

/**
 * Type-to-select within the active column, matching Finder: jump to the next
 * sibling whose name starts with the typed prefix, wrapping around so repeated
 * presses of one letter cycle.
 */
export const typeToSelect = (
  tree: FsTree,
  path: string[],
  prefix: string
): string[] => {
  if (!prefix) return path;
  const valid = prunePath(tree, path);
  const siblings =
    valid.length === 0 ? childrenOf(tree, null) : siblingsFor(tree, valid);
  if (siblings.length === 0) return valid;

  const depth = Math.max(valid.length - 1, 0);
  const currentIndex = siblings.findIndex((n) => n.id === valid[depth]);
  const needle = prefix.toLowerCase();

  for (let step = 1; step <= siblings.length; step += 1) {
    const candidate =
      siblings[(Math.max(currentIndex, 0) + step) % siblings.length];
    if (candidate.name.toLowerCase().startsWith(needle)) {
      return selectAtDepth(valid, depth, candidate.id);
    }
  }

  // Also accept the current item, so holding a letter down does not deselect.
  const current = siblings[currentIndex];
  if (current && current.name.toLowerCase().startsWith(needle)) return valid;

  return valid;
};

/**
 * Whether `nodeId` can be moved under `targetId`, used to reject a drop before
 * it round-trips. A folder cannot land inside itself or its own descendants,
 * and nothing can land inside a file.
 */
export const canDropInto = <N extends TreeNodeLike>(
  tree: FsTree<N>,
  nodeIds: string[],
  targetId: string | null
): boolean => {
  if (targetId === null) return true;
  const target = tree.byId.get(targetId);
  if (!target || target.kind !== "folder") return false;
  if (nodeIds.includes(targetId)) return false;

  const moving = new Set(nodeIds);
  let cursor: N | undefined = target;
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor.id)) {
    seen.add(cursor.id);
    if (moving.has(cursor.id)) return false;
    cursor = cursor.parent_id ? tree.byId.get(cursor.parent_id) : undefined;
  }

  return true;
};

/**
 * Reorders a selection optimistically so the UI can renumber exhibits in the
 * same frame as the drag, before the server confirms the new sort keys.
 */
export const applyOptimisticMove = <N extends TreeNodeLike>(
  nodes: N[],
  nodeIds: string[],
  targetParentId: string | null,
  position?: { beforeId?: string | null; afterId?: string | null }
): N[] => {
  const moving = new Set(nodeIds);
  const tree = buildTree(nodes);
  const siblings = childrenOf(tree, targetParentId).filter(
    (n) => !moving.has(n.id)
  );

  // The bounds the moved block slots between, mirroring the server's
  // `_bounds_for_position`. Only the moved nodes are rekeyed; every sibling
  // keeps its real fractional key, so the move endpoint's response — which
  // likewise returns only the moved nodes — merges back without desyncing.
  const keys = siblings.map((n) => n.sort_key);
  const indexById = new Map(siblings.map((n, index) => [n.id, index]));

  let lower: string | null;
  let upper: string | null;
  if (position?.afterId != null && indexById.has(position.afterId)) {
    const index = indexById.get(position.afterId) as number;
    lower = keys[index];
    upper = index + 1 < keys.length ? keys[index + 1] : null;
  } else if (position?.beforeId != null && indexById.has(position.beforeId)) {
    const index = indexById.get(position.beforeId) as number;
    lower = index > 0 ? keys[index - 1] : null;
    upper = keys[index];
  } else {
    lower = keys.length ? keys[keys.length - 1] : null;
    upper = null;
  }

  // Ordered by `nodeIds` so the dragged block keeps its own relative order,
  // matching how the server zips the ids to the generated keys.
  const dragged = nodeIds
    .map((id) => tree.byId.get(id))
    .filter((node): node is N => node !== undefined);
  const provisionalKeys = keysBetween(lower, upper, dragged.length);
  const provisional = new Map(
    dragged.map((node, index) => [node.id, provisionalKeys[index]])
  );

  return nodes.map((node) => {
    if (!moving.has(node.id)) return node;
    return {
      ...node,
      parent_id: targetParentId,
      sort_key: provisional.get(node.id) as string
    };
  });
};
